import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AuthzMode,
  callAccountOrganization,
  listAuthzGrants,
  listAuthzRoles,
  Role,
  type AuthzGrant,
  type AuthzOrgRole,
} from '@/api'
import { useAuth } from '@/hooks/use-auth'
import { useAuthzCatalogue } from '@/hooks/use-authz-catalogue'
import { useFeatureFlags } from '@/hooks/use-feature-flags'
import { grantFromApi, rolesFromApi, type AuthzState } from '@/lib/authz/api'
import type { Catalogue } from '@/lib/authz/catalogue'
import { FeatureNames } from '@/lib/featureFlags'
import { getApiErrorStatus } from '@/utils/apiError'

export const authzQueryKeys = {
  all: (oid: string | undefined) => ['authz', oid ?? null] as const,
  roles: (oid: string | undefined) => ['authz', oid ?? null, 'roles'] as const,
  grants: (oid: string | undefined) =>
    ['authz', oid ?? null, 'grants'] as const,
  mode: (oid: string | undefined) => ['authz', oid ?? null, 'mode'] as const,
}

const STALE_MS = 30_000

// Un refus (403 : organisation pas basculée, ou aucune agence visible) ne change pas en réessayant.
const retryOnServerError = (failureCount: number, error: unknown): boolean => {
  const status = getApiErrorStatus(error)
  return (status === undefined || status >= 500) && failureCount < 2
}

/** Les rôles personnalisés de l'organisation du membre connecté. */
export const useAuthzRoles = (enabled = true) => {
  const { user } = useAuth()
  const oid = user?.organization
  return useQuery({
    queryKey: authzQueryKeys.roles(oid),
    queryFn: async () => (await listAuthzRoles(oid as string)).data,
    enabled: enabled && Boolean(oid),
    staleTime: STALE_MS,
    retry: retryOnServerError,
  })
}

/** Les accès de l'organisation du membre connecté, sur les agences où il voit les membres. */
export const useAuthzGrants = (enabled = true) => {
  const { user } = useAuth()
  const oid = user?.organization
  return useQuery({
    queryKey: authzQueryKeys.grants(oid),
    queryFn: async () => (await listAuthzGrants(oid as string)).data,
    enabled: enabled && Boolean(oid),
    staleTime: STALE_MS,
    retry: retryOnServerError,
  })
}

/**
 * Qui décide des droits dans l'organisation du membre (ADR 0033) : les rôles d'avant (`legacy`), encore
 * eux pendant l'observation, ou les accès (`active`). Une gateway qui ne le dit pas compte comme legacy.
 */
export const useOrganizationAuthzMode = (enabled = true) => {
  const { user } = useAuth()
  const oid = user?.organization
  return useQuery({
    queryKey: authzQueryKeys.mode(oid),
    queryFn: async () =>
      (await callAccountOrganization()).authzMode ?? AuthzMode.Legacy,
    enabled: enabled && Boolean(oid),
    staleTime: STALE_MS,
    retry: retryOnServerError,
  })
}

const NO_ROLES: AuthzOrgRole[] = []
const NO_GRANTS: AuthzGrant[] = []
const NO_ROLE_STATE: Pick<AuthzState, 'roles' | 'customCapacities'> = {
  roles: [],
  customCapacities: [],
}

/**
 * Rôles et accès de l'organisation dans le modèle de l'écran, avec le catalogue qui les lit : sans lui,
 * aucun rôle. Traduits séparément : un accès donné ailleurs ne doit pas recréer les rôles, ce qui
 * effacerait les cases en cours de réglage.
 */
export const useAuthzState = (
  enabled = true
): {
  state: AuthzState
  catalogue: Catalogue | undefined
  loading: boolean
  failed: boolean
} => {
  const catalogue = useAuthzCatalogue(enabled)
  const roles = useAuthzRoles(enabled)
  const grants = useAuthzGrants(enabled)
  const roleState = useMemo(
    () =>
      catalogue.data
        ? rolesFromApi(catalogue.data, roles.data ?? NO_ROLES)
        : NO_ROLE_STATE,
    [catalogue.data, roles.data]
  )
  const grantList = useMemo(
    () => (grants.data ?? NO_GRANTS).map(grantFromApi),
    [grants.data]
  )
  const state = useMemo<AuthzState>(
    () => ({ ...roleState, grants: grantList }),
    [roleState, grantList]
  )
  return {
    state,
    catalogue: catalogue.data,
    loading:
      catalogue.isInitialLoading ||
      roles.isInitialLoading ||
      grants.isInitialLoading,
    failed: catalogue.isError || roles.isError || grants.isError,
  }
}

/**
 * Les écrans des rôles et permissions (entrée Rôles, onglet Accès, rôle dans la liste des membres)
 * s'ouvrent à trois conditions : le flag `roles_permissions`, que la console cible par rôle ou par
 * personne ; une organisation en mode actif, où les accès décident (Gerald, 19/10/2026 : avant, un
 * client modifierait des droits sans effet) ; et des accès pour le membre (un SUPERADMIN : dans
 * l'organisation). Ailleurs, les écrans restent ceux d'avant les rôles.
 */
export const useAuthzScreens = (): { enabled: boolean; isLoaded: boolean } => {
  const { user } = useAuth()
  const { isFeatureEnabled, isLoaded: flagsLoaded } = useFeatureFlags()
  const flag = isFeatureEnabled(FeatureNames.rolesPermissions)
  const mode = useOrganizationAuthzMode(flag)
  const active = mode.data === AuthzMode.Active
  const grants = useAuthzGrants(flag && active)
  const visible = grants.data ?? []
  const switched =
    user?.role === Role.Superadmin
      ? visible.length > 0
      : visible.some((g) => g.userId === user?.uid)
  return {
    enabled: flag && active && switched,
    isLoaded:
      flagsLoaded &&
      (!flag ||
        !user?.organization ||
        mode.isError ||
        (mode.isSuccess && (!active || grants.isSuccess || grants.isError))),
  }
}
